import {
  MediaFormat,
  getCropZoneParam,
  getFirstMedia,
  getImageFromMedias,
  getMediasSite,
  getVideoThumbnail,
  parseMedias,
  resizer,
} from '@prismamedia/one-components';
import {
  Article,
  ArticleFormat,
  ArticleStatus,
  BrandKey,
} from '__generated__/queries-web';
import { RawDraftContentBlock, RawDraftContentState } from 'draft-js';
import { startsWith as _startsWith, when as _when, find } from 'ramda';
import { embedImage } from 'utils/useUploadImage';
import { MEDIA_TYPES_LABEL } from './MultimediaPlugin/types';

interface SlideshowData {
  id: string;
  description: RawDraftContentState | null;
  hasButton: boolean;
  buttonUrl: string | null;
  buttonTitle: string | null;
  buttonRel: string | null;
}

interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MediaBlockData {
  caption: string;
  iframely: any;
  slideshow?: SlideshowData;
  focusPoint: {
    x: number | null;
    y: number | null;
    relativeX: number | null;
    relativeY: number | null;
  };
  crop?: Crop;
  type: 'media';
}

type MediaBlock = RawDraftContentBlock & {
  data: MediaBlockData;
};

export const baseRecipesURLs: Record<string, string> = {
  FAC: 'https://www.femmeactuelle.fr',
  CAC: 'https://www.cuisineactuelle.fr',
};

export const standardizeArticleUrl = (url: string | null): string | null => {
  if (!url) return null;
  return !url.startsWith('http') ? `https://${url}` : url;
};

const getUrlForDiapo = (domain: string, path: string) => {
  const removeSlash = _when(_startsWith('/'), (data) => data.slice(1));
  // eslint-disable-next-line no-param-reassign
  path = removeSlash(path);
  // eslint-disable-next-line no-param-reassign
  domain = removeSlash(domain);
  return `${domain}${path}`;
};

const embedMediaFromArticle = async (
  article: Pick<Article, 'urls' | 'medias'>,
  articleFormat: ArticleFormat,
) => {
  let mediaUrl = '';

  if (articleFormat === ArticleFormat.Slideshow) {
    const [{ origin, path }] = article.urls;
    mediaUrl = getUrlForDiapo(origin, path);
  } else if (
    [ArticleFormat.Video, ArticleFormat.Rich].includes(articleFormat)
  ) {
    const media = getFirstMedia(parseMedias(article.medias), MediaFormat.Video);
    if (media) {
      mediaUrl = media.iframely.meta.canonical;
    }
  }

  return embedImage(mediaUrl);
};

const statusToLabel = (
  format: ArticleFormat,
): Record<ArticleStatus, string> => {
  const videoStatus = format === 'Video';
  return {
    ['Published']: videoStatus ? 'Vidéo Publique' : 'Publié',
    ['Scheduled']: 'Programmé',
    ['Draft']: 'Brouillon',
    ['Frozen']: 'Gelé',
    ['Ready']: videoStatus ? 'Vidéo Prête' : 'Prêt',
    ['Deleted']: 'Corbeille',
    ['Pending']: 'En cours',
    ['Proposal']: 'En proposition',
    ['Todo']: 'A faire',
    ['Private']: videoStatus ? 'Vidéo Privée' : 'Privé',
    ['ScheduledForRepublication']: 'Programmé pour publication',
  };
};

const getImageFromArticleBody = (
  body: RawDraftContentState | null,
  withCrop: boolean,
): string | undefined => {
  if (body) {
    if (!Array.isArray(body)) {
      const media = find((block: MediaBlock) => block.data.type === 'media')(
        body.blocks as MediaBlock[],
      );
      const path = media?.data.iframely.meta.url;
      if (!withCrop) {
        return resizer.fit({
          origin: path,
          width: 350,
          height: 150,
        });
      }
      const crop = media?.data.crop;
      return resizer.fit({
        origin: path,
        width: 350,
        height: 150,
        parameters: getCropZoneParam(crop),
      });
    }
  }
};

export const getItemToRender = (
  brandKey: BrandKey,
  item: any,
  pluginType?: string,
) => {
  switch (pluginType) {
    case MEDIA_TYPES_LABEL.ARTICLES:
      return {
        chip: item.brandKey,
        id: item.id,
        image:
          getImageFromMedias(item.medias)?.mediaForDisplay ||
          item?.imageSrc?.mediaForDisplay,
        publicUrl: standardizeArticleUrl(item?.canonical) || item?.url,
        publishDate: item.publishedAt,
        text: item.creator?.name,
        title: item.title,
      };
    case MEDIA_TYPES_LABEL.RECIPES:
      return {
        chip: item.brandKey,
        id: item.id,
        image:
          getImageFromMedias(item.medias)?.mediaForDisplay ||
          item?.imageSrc?.mediaForDisplay,
        publicUrl:
          item?.url || baseRecipesURLs[brandKey] + item.recipeUrls[0].url?.path,
        publishDate: item.publishedAt,
        text: item.creator?.name,
        title: item.title,
      };
    case MEDIA_TYPES_LABEL.VIDEOS:
      return {
        chip: item.brandKey,
        id: item.id,
        image: getVideoThumbnail(item.medias),
        publicUrl: standardizeArticleUrl(item?.contextualizedUrl),
        publishDate: item.publishedAt,
        site: getMediasSite(item.medias)?.[0],
        status: `${
          (statusToLabel(ArticleFormat.Video) as any)[item.status]
        } le`,
        text: item.creator?.name,
        title: item.title,
      };
    case MEDIA_TYPES_LABEL.SLIDESHOWS:
      return {
        chip: item.brandKey,
        id: item.id,
        image: getImageFromArticleBody(item.body, false),
        publicUrl: standardizeArticleUrl(item?.contextualizedUrl),
        publishDate: item.publishedAt,
        text: item.creator?.name,
        title: item.title,
      };
    default:
      return {
        id: 'no-plugin-type',
      };
  }
};

const getRecipeMediaUrl = (medias: string | null) => {
  const imageSrc = getImageFromMedias(medias);

  if (imageSrc?.media) {
    return imageSrc.media.iframely.meta.canonical;
  }
};

export const getItemToIframely = async (item: any, pluginType: string) => {
  switch (pluginType) {
    case MEDIA_TYPES_LABEL.ARTICLES:
      return await embedMediaFromArticle(item, ArticleFormat.Rich);
    case MEDIA_TYPES_LABEL.RECIPES:
      return await embedImage(getRecipeMediaUrl(item.medias));
    case MEDIA_TYPES_LABEL.VIDEOS:
      return await embedMediaFromArticle(item, ArticleFormat.Video);
    case MEDIA_TYPES_LABEL.SLIDESHOWS:
      return await embedMediaFromArticle(item, ArticleFormat.Slideshow);
    default:
      throw new Error('getItemToIframely failed: unknown pluginType');
  }
};

export const SEARCH_PAGE_RESULT = 50;
export const DEFAULT_PAGE_RESULT = 25;
